import {
    Component,
    inject,
    Injectable,
    OnInit,
    signal,
    ViewChild
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import {
    CommonModule
} from "@angular/common";

import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";

import {
    MatDividerModule
} from "@angular/material/divider";

import {
    MatAutocompleteModule
} from "@angular/material/autocomplete";

import {
    MatButtonModule
} from "@angular/material/button";

import {
    MatOptionModule
} from "@angular/material/core";

import {
    MatFormFieldModule
} from "@angular/material/form-field";

import {
    MatIconModule
} from "@angular/material/icon";

import {
    MatInputModule
} from "@angular/material/input";

import {
    MatListModule,
    MatSelectionList,
    MatSelectionListChange
} from "@angular/material/list";

import {
    MatRadioModule
} from "@angular/material/radio";

import {
    MatSelectModule
} from "@angular/material/select";

import {
    MatSlideToggleModule
} from "@angular/material/slide-toggle";

import {
    MatTabsModule
} from "@angular/material/tabs";

import {
    TranslateModule,
    TranslateService
} from "@ngx-translate/core";

import {
    lastValueFrom
} from "rxjs";

import {
    ConfigurationManagerService
} from "src/app/core";

import {
    ZPMSApi,
    ZPMSConfig,
    ZPMSConnectorApiService
} from "../z-pms-connector-api.service";

import {
    ZPMSConnector
} from "../z-pms-connector.model";

import {
    PaymentMethods,
} from "tilby-models";

import {
    keyBy,
    MathUtils
} from "src/app/shared/utils";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogProgressBarComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    EnvironmentConfig
} from "src/environments/environment-config";

type ZPMSConnectorDialogInput = {
    amount: number;
    paymentMethod: PaymentMethods;
}

@Component({
    selector: 'z-pms-connector-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatRadioModule,
        MatOptionModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatTabsModule,
        ReactiveFormsModule,
        TilbyCurrencyPipe,
        TilbyDialogContentComponent,
        TilbyDialogProgressBarComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
    ],
    templateUrl: './z-pms-connector-dialog.component.html',
    styleUrls: []
})
class ZPMSConnectorDialogComponent implements OnInit {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly translateService = inject(TranslateService);
    private readonly zPMSConnectorApi = inject(ZPMSConnectorApiService);
    public data: ZPMSConnectorDialogInput = inject(MAT_DIALOG_DATA);

    //PMS configuration parameters
    private apiHandler?: ZPMSApi;

    //UI params
    protected readonly amountToPay = MathUtils.round(this.data.amount);
    protected readonly showAmount = !(this.configurationManagerService.getPreference('pms_payments.hide_total_amount'));
    protected readonly showBalance = !(this.configurationManagerService.getPreference('pms_payments.hide_balance'));
    protected readonly paymentMethod = this.data.paymentMethod;

    protected loginForm?: FormGroup;
    protected foliosForm?: FormGroup;

    protected message = '';
    protected folios: ZPMSConnector.FolioDto[] = [];
    protected roomFolios: ZPMSConnector.FolioDto[] = [];
    protected miscSalesFolios?: ZPMSConnector.FolioDto[];
    protected supportedPMSData: Record<string, ZPMSConnector.SupportedPmsItem> = {};
    protected supportedPMS: string[] = [];
    protected operationInProgress = signal(false);
    protected searchText = '';
    protected selectedFolio?: ZPMSConnector.FolioDto;
    protected visibleRoomFolios?: ZPMSConnector.FolioDto[] = [];
    protected visibleMiscSalesFolios?: ZPMSConnector.FolioDto[];
    protected filterInput = new FormControl('');

    //Disabled due to lack of a staging environment in new APIs
    protected showStagingFlag = EnvironmentConfig.environment !== 'stable' && false;

    @ViewChild('roomFolioList') roomFolioList?: MatSelectionList;
    @ViewChild('miscFolioList') miscFolioList?: MatSelectionList;

    /**
     * Sets up the PMS connection based on the login form data.
     * Saves the configuration and then attempts to fetch folios.
     */
    async setupShop() {
        if (!this.loginForm?.valid) {
            return;
        }

        this.operationInProgress.set(true);

        const connectedPms = this.loginForm?.get('connectedPms')?.value;

        const setupData: ZPMSConfig = {
            address: this.loginForm?.get('address')?.value || undefined,
            apiKey: this.loginForm?.get('apiKey')?.value || undefined,
            pmsFeatures: this.supportedPMSData[connectedPms] || undefined,
            connectedPms: connectedPms,
            propertyId: this.loginForm?.get('propertyId')?.value || undefined,
            stagingMode: this.showStagingFlag ? !!this.loginForm?.get('stagingMode')?.value : undefined,
            username: this.loginForm?.get('username')?.value || undefined,
            password: this.loginForm?.get('password')?.value || undefined,
        };

        if (setupData.connectedPms === 'Suite' && setupData.address) {
            if (!setupData.address.endsWith('/TbyApi/application')) {
                setupData.address = setupData.address.replace(/\/$/, '') + '/TbyApi/application';
            }
        }

        try {
            //Setup API manager (handles saving of parameters into the shop_preferences)
            try {
                await this.zPMSConnectorApi.setupApi(this.paymentMethod, setupData);
                this.message = '';

                this.fetchFolios();
            } catch (err: any) {
                this.message = this.translateService.instant(err);
            }
        } finally {
            this.operationInProgress.set(false);
        }
    };

    /**
     * Checks if a specific form field is required based on the PMS features.
     * @param fieldName The name of the form field.
     * @param pmsFeatures The PMS features object.
     * @returns True if the field is required, false otherwise.
     */
    private isFieldRequired(fieldName: string, pmsFeatures: ZPMSConnector.SupportedPmsItem) {
        switch (fieldName) {
            case 'apiKey':
                return !!(pmsFeatures.credentials?.apiKey);
            case 'username':
            case 'password':
                return !!(pmsFeatures.credentials?.basicAuth);
            case 'propertyId':
                return !!(pmsFeatures.credentials?.propertyId);
            case 'address':
                return !!(pmsFeatures.credentials?.address);
            default:
                return false;
        }
    }

    /**
     * Handles changes to the 'connectedPms' field in the login form, dynamically setting validators.
     * @param loginForm The login FormGroup.
     * @param value The selected PMS value.
     */
    private onConnectedPMSChange(loginForm: FormGroup, value?: string) {
        const pmsFeatures = this.supportedPMSData[value!] || {};

        for (const [field, formControl] of Object.entries(loginForm.controls)) {
            if (['connectedPms', 'stagingMode'].includes(field)) {
                continue;
            }

            if (this.isFieldRequired(field, pmsFeatures)) {
                formControl.setValidators([Validators.required]);
            } else {
                formControl.clearValidators();
            }

            formControl.updateValueAndValidity();
        }
    }

    /**
     * Fetches the supported PMS features from the API.
     * @param stagingMode Optional boolean to indicate staging mode.
     */
    private async getPMSFeatures(stagingMode?: boolean) {
        try {
            const apiInstance = await this.zPMSConnectorApi.getApiInstance({
                ...this.paymentMethod,
                configuration: { stagingMode: !!stagingMode }
            }, { skipConfigCheck: true });

            this.supportedPMSData = await apiInstance.getSupportedPMS()
                .then(response =>  keyBy(response, val => val.name));

            this.supportedPMS = Object.keys(this.supportedPMSData).sort((a, b) => a.localeCompare(b));
        } catch (err) {
            console.error(err);
        }
    }

    private static loginFields: (keyof ZPMSConfig)[] = [
        'address',
        'apiKey',
        'connectedPms',
        'password',
        'propertyId',
        'username',
        'stagingMode',
    ];

    /**
     * Creates and initializes the login form for PMS configuration.
     */
    private async createLoginForm() {
        this.roomFolios = [];
        this.miscSalesFolios = [];
        this.foliosForm = undefined;

        const formFields: Record<string, any[]> = {};
        const currentConfiguration: Partial<ZPMSConfig> = this.data.paymentMethod.configuration || {};

        for (const key of ZPMSConnectorDialogComponent.loginFields) {
            formFields[key] = [currentConfiguration[key], []];
        }

        const loginForm = this.formBuilder.group(formFields);

        // Get supported PMS if not already done
        if (!this.supportedPMS.length) {
            await this.getPMSFeatures(!!currentConfiguration.stagingMode);
        }

        //Initialize required flag for connectedPms
        this.onConnectedPMSChange(loginForm, loginForm.get('connectedPms')?.value!);

        loginForm.get('connectedPms')?.valueChanges.subscribe((value) => {
            this.onConnectedPMSChange(loginForm, value!);
        });

        loginForm.get('stagingMode')?.valueChanges.subscribe((value) => {
            this.getPMSFeatures(value);
        })

        this.loginForm = loginForm;
    }


    /**
     * Creates and initializes the folios form.
     */
    private createFoliosForm() {
        this.loginForm = undefined;

        this.foliosForm = this.formBuilder.group({
        });
    }

    /**
     * Applies a filter to the room and misc sales folios.
     * @param filterValue The string value to filter by.
     */
    protected applyFilter(filterValue: string | null) {
        if (!this.folios) {
            return;
        }

        if (!filterValue) {
            this.visibleRoomFolios = [...this.roomFolios];
            this.visibleMiscSalesFolios = this.miscSalesFolios ? [...this.miscSalesFolios] : undefined;
            return;
        }

        const filter = filterValue.trim().toLowerCase();

        if (this.roomFolios) {
            this.visibleRoomFolios = (this.roomFolios || []).filter(folio => {
                const guestName = folio.guestNames?.[0]?.firstName?.toLowerCase() + ' ' + folio.guestNames?.[0]?.lastName?.toLowerCase() || '';
                const companyName = folio.companyName?.toLowerCase() || '';
                const keyDoorCode = folio.keyDoorCode?.toLowerCase() || '';

                return guestName.includes(filter) || companyName.includes(filter) || keyDoorCode.includes(filter);
            });
        } else {
            this.visibleRoomFolios = undefined;
        }

        if (this.miscSalesFolios) {
            this.visibleMiscSalesFolios = (this.miscSalesFolios || []).filter(folio => {
                const guestName = folio.guestNames?.[0]?.firstName?.toLowerCase() + ' ' + folio.guestNames?.[0]?.lastName?.toLowerCase() || '';
                const companyName = folio.companyName?.toLowerCase() || '';

                return guestName.includes(filter) || companyName.includes(filter);
            });
        } else {
            this.visibleMiscSalesFolios = undefined;
        }

    }

    /**
     * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
     */
    ngOnInit() {
        this.fetchFolios();

        this.filterInput.valueChanges.subscribe(filterValue => {
            this.applyFilter(filterValue);
        });
    }

    /**
     * Fetches folios from the PMS connector API.
     * If an error occurs, it creates a login form for configuration.
     */
    private async fetchFolios() {
        this.createFoliosForm();
        this.operationInProgress.set(true);

        try {
            this.apiHandler = await this.zPMSConnectorApi.getApiInstance(this.paymentMethod);

            try {
                this.roomFolios = await this.apiHandler?.getReservations();
                this.miscSalesFolios = await this.apiHandler?.getMiscSales().catch(() => undefined)

                this.applyFilter(null);
            } catch (error: any) {
                if (error === undefined) {
                    throw this.translateService.instant('CASHREGISTER.ERICSOFT_PMS_PAYMENTS.OFFLINE');
                } else {
                    throw error?.msg || '';
                }
            }
        } catch (error: any) {
            this.message = error;
            this.createLoginForm();
            return;
        } finally {
            this.operationInProgress.set(false);
        }
    };

    /**
     * Clears the current message displayed in the dialog.
     */
    clearMessage() {
        this.message = '';
    }

    /**
     * Handles the selection of a folio from either the room or misc folios list.
     * Ensures only one folio is selected across both lists.
     * @param event The MatSelectionListChange event.
     * @param tab The tab from which the selection originated ('room' or 'misc').
     */
    onFolioSelect(event: MatSelectionListChange, tab: 'room' | 'misc') {
        if (!event.options.length) {
            this.selectedFolio = undefined;
            return;
        }

        this.selectedFolio = event.options[0].value;

        if (tab === 'room' && this.miscFolioList) {
            this.miscFolioList.deselectAll();
        } else if (tab === 'misc' && this.roomFolioList) {
            this.roomFolioList.deselectAll();
        }
    }

    /**
     * Confirms the selected folio and closes the dialog, passing the selected folio data.
     */
    confirm() {
        if (this.selectedFolio) {
            this.matDialogRef.close(this.selectedFolio);
        }
    };
}

@Injectable({
    providedIn: 'root'
})
export class ZPMSConnectorDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    /**
     * Opens the ZPMS Connector dialog.
     * @param config Configuration data for the dialog, including amount and payment method.
     * @returns A Promise that resolves with the selected ZPMS FolioDto or undefined if cancelled.
     */
    public openDialog(config: NonNullableConfigData<ZPMSConnectorDialogInput>): Promise<ZPMSConnector.FolioDto | undefined> {
        return lastValueFrom(this.dialogRef.open(ZPMSConnectorDialogComponent, {
            ...this.switchMobileDesktopDimensions({ width: '800px' }),
            ...config,
        }).afterClosed());
    }
}